import React, { useCallback } from "react";
import PurchaseDocDetails from "../../_components/Purchasing/PurchaseDocDetails";
import {
    getPurchaseOrderById,
    updatePurchaseOrder,
    createPurchaseOrder,
} from "../../_services/purchase.service";
import { getSuppliers } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";
import { isPurchasingManager, isSuperAdmin, canAccessAccounting } from '../../utils/roleConstants';

/* Supplier data fetchers */
let supplierCache = {};
async function getCachedSupplier(supplierName) {
    if (!supplierName) return [];
    if (supplierCache[supplierName]) return supplierCache[supplierName];
    const res = await getSuppliers({
        pageSize: 1000,
        page: 1,
        search: supplierName,
        searchType: "companyName",
    });
    supplierCache[supplierName] = res?.data || [];
    return supplierCache[supplierName];
}

async function fetchSupplierNames() {
    const res = await getSuppliers({ pageSize: 1000, page: 1 });
    const names = (res?.data || [])
        .filter(r => {
            const s = (r.status || r.Status || "").toLowerCase();
            return s === "active";
        })
        .map((r) => r.name)
        .filter(Boolean);
    return [...new Set(names)].sort();
}

async function fetchContactPersons(supplierName) {
    if (!supplierName) return [];
    const data = await getCachedSupplier(supplierName);
    return data
        .filter((r) => {
            const s = (r.status || r.Status || "").toLowerCase();
            return r.name === supplierName && !!r.attention && s === "active";
        })
        .map((r) => r.attention ?? "")
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i);
}

async function fetchSupplierDetails(supplierName, contactPerson = "") {
    if (!supplierName) return {};

    const data = await getCachedSupplier(supplierName);
    const rows = data.filter((r) => r.name === supplierName);
    const first = rows[0];

    if (!first) return {};

    // Always return addresses when supplier is selected
    const base = {
        supplierAddress: first.billingAddress || first.billing_address || first.address || "",
        supplierShippingAddress: first.shippingAddress || first.shipping_address || "",
    };

    // Only return phone + payment terms when contact person is selected
    if (!contactPerson) return base;

    const match = rows.find((r) => (r.attention || "") === contactPerson) || first;

    return {
        ...base,
        paymentTerms: match.creditTerms || match.credit_terms || "",
        phoneNumber: match.phoneNumber || match.phone_number || "",
    };
}

export default function PurchaseOrderDetails() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // BUM can only view — hide create button
    const viewOnly = isPurchasingManager(user) && !canAccessAccounting(user) && !isSuperAdmin(user);
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);

    return (
        <PurchaseDocDetails
            docType="Purchase Order"
            basePath="/purchases/purchase-orders"
            fetchById={getPurchaseOrderById}
            updateItem={updatePurchaseOrder}
            createItem={createPurchaseOrder}
            fetchSupplierNames={fetchSupplierNames}
            fetchContactPersons={fetchContactPersons}
            fetchSupplierDetails={fetchSupplierDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            searchable={true}
            searchSource="RM"
            viewOnly={viewOnly}
        />
    );
}
